// ============= Types =============
import { PaymentRow } from '../components/container/forms/schemas';
import { PaymentWorkflowType } from '../components/container/forms/types/api/graph.types';

export interface FormDataFormatted extends Omit<
  FormData,
  'createdDate' | 'submittedDate'
> {
  createdDate: string;
  submittedDate: string;
  workflowType?: PaymentWorkflowType;
}

/**
 * Represents the main form structure
 */
export interface FormData {
  createdBy?: string;
  createdByEmail?: string | null;
  createdByName: string;
  createdDate: string;
  id: number;
  lastModifiedAt: string;
  name: string;
  sections: FormSection[];
  status: string;
  submittedDate: string;
  userIsCreator?: boolean;
  workflowType?: PaymentWorkflowType;
}

/**
 * Represents a section in the form
 */
export interface FormSection {
  assignedToUserId: string | null;
  completedDate: string | null;
  fields?: SectionFields;
  gridData?: PaymentRow[] | PaymentRow | null;
  isComplete: boolean;
  sectionKey: SectionKey;
  documents?: Record<string, unknown> | null;
}

export type SearchType =
  | 'feeEarner'
  | 'partner'
  | 'client'
  | 'matter'
  | 'default';

/**
 * Available section keys in the form
 * Organized by workflow type for clarity
 */
export type SectionKey =
  // Standard workflow sections (Dubai Payment Approval)
  | 'payment_details'
  | 'resp_lawyer'
  | 'first_partner'
  | 'second_partner'
  | 'second_partner_above_25k'
  | 'second_partner_below_25k'
  | 'payment_hold_review'
  | 'payment_confirmation'
  | 'document_saving'
  | 'request_reassign'
  // Special case for rejected forms - uses API's currentSection
  | 'rejected_form'
  // Dubai Payment From Office Account workflow sections
  | 'dubai_payment_from_office_account_payment_details'
  | 'dubai_payment_from_office_account_approver'
  | 'dubai_payment_from_office_account_payment_confirmation'
  // Dubai Transfer Requisition workflow sections
  | 'dubai_transfer_requisition_payment_details'
  | 'dubai_transfer_requisition_first_partner'
  | 'dubai_transfer_requisition_second_partner'
  | 'dubai_transfer_requisition_payment_confirmation'
  // Overtime Approval workflow sections
  | 'overtime_approval_request_details'
  | 'overtime_approval_line_manager'
  | 'overtime_approval_finance_confirmation'
  | 'gift_hospitality_donation_uk_request_details'
  | 'gift_hospitality_donation_uk_approval'
  | 'gift_hospitality_donation_dubai_request_details'
  | 'gift_hospitality_donation_dubai_approval'
  | 'gift_hospitality_donation_dublin_request_details'
  | 'gift_hospitality_donation_dublin_approval';
  // PLOP_INJECT_SECTION_KEY

export type SectionKeyToDisplayMapper = {
  // Standard workflow sections
  payment_details: 'Payment Details';
  resp_lawyer: 'Responsible Lawyer';
  first_partner: 'First Partner';
  second_partner: 'Second Partner';
  second_partner_above_25k: 'Second Partner (Above 25k)';
  second_partner_below_25k: 'Second Partner (Below 25k)';
  payment_confirmation: 'Payment Confirmation';
  payment_hold_review: 'Payment Hold Review';
  document_saving: 'Document Saving';
  request_reassign: 'Request Reassign';
  rejected_form: 'Rejected Form';
  // Dubai Payment From Office Account workflow sections
  dubai_payment_from_office_account_payment_details: 'Payment Details';
  dubai_payment_from_office_account_approver: 'Approver';
  dubai_payment_from_office_account_payment_confirmation: 'Payment Confirmation';
  // Dubai Transfer Requisition workflow sections
  dubai_transfer_requisition_payment_details: 'Payment Details';
  dubai_transfer_requisition_first_partner: 'First Partner';
  dubai_transfer_requisition_second_partner: 'Second Partner';
  dubai_transfer_requisition_payment_confirmation: 'Payment Confirmation';
  // Overtime Approval workflow sections
  overtime_approval_request_details: 'Request Details';
  overtime_approval_line_manager: 'Line Manager Approval';
  overtime_approval_finance_confirmation: 'Finance Confirmation';
  gift_hospitality_donation_uk_request_details: 'Request Details';
  gift_hospitality_donation_uk_approval: 'Approval';
  gift_hospitality_donation_dubai_request_details: 'Request Details';
  gift_hospitality_donation_dubai_approval: 'Approval';
  gift_hospitality_donation_dublin_request_details: 'Request Details';
  gift_hospitality_donation_dublin_approval: 'Approval';
  // PLOP_INJECT_SECTION_DISPLAY_TYPE
};

export const sectionKeyToDisplay: SectionKeyToDisplayMapper = {
  // Standard workflow sections
  payment_details: 'Payment Details',
  resp_lawyer: 'Responsible Lawyer',
  first_partner: 'First Partner',
  second_partner: 'Second Partner',
  second_partner_above_25k: 'Second Partner (Above 25k)',
  second_partner_below_25k: 'Second Partner (Below 25k)',
  payment_confirmation: 'Payment Confirmation',
  payment_hold_review: 'Payment Hold Review',
  document_saving: 'Document Saving',
  request_reassign: 'Request Reassign',
  rejected_form: 'Rejected Form',
  // Dubai Payment From Office Account workflow sections
  dubai_payment_from_office_account_payment_details: 'Payment Details',
  dubai_payment_from_office_account_approver: 'Approver',
  dubai_payment_from_office_account_payment_confirmation:
    'Payment Confirmation',
  // Dubai Transfer Requisition workflow sections
  dubai_transfer_requisition_payment_details: 'Payment Details',
  dubai_transfer_requisition_first_partner: 'First Partner',
  dubai_transfer_requisition_second_partner: 'Second Partner',
  dubai_transfer_requisition_payment_confirmation: 'Payment Confirmation',
  // Overtime Approval workflow sections
  overtime_approval_request_details: 'Request Details',
  overtime_approval_line_manager: 'Line Manager Approval',
  overtime_approval_finance_confirmation: 'Finance Confirmation',
  gift_hospitality_donation_uk_request_details: 'Request Details',
  gift_hospitality_donation_uk_approval: 'Approval',
  gift_hospitality_donation_dubai_request_details: 'Request Details',
  gift_hospitality_donation_dubai_approval: 'Approval',
  gift_hospitality_donation_dublin_request_details: 'Request Details',
  gift_hospitality_donation_dublin_approval: 'Approval',
  // PLOP_INJECT_SECTION_DISPLAY_MAP
};

export type GetDisplayName<T extends SectionKey> = SectionKeyToDisplayMapper[T];

/**
 * Mapping from backend section names to frontend section keys
 * Note: This mapping may need to be context-aware based on workflow type
 */
export const backendSectionToSectionKey: Record<string, SectionKey> = {
  // Standard workflow sections
  'Payment Details': 'payment_details',
  'Responsible Lawyer': 'resp_lawyer',
  'First Partner': 'first_partner',
  'Second Partner': 'second_partner',
  'Second Partner (Above 25k)': 'second_partner_above_25k',
  'Second Partner (Below 25k)': 'second_partner_below_25k',
  'Payment Confirmation': 'payment_confirmation',
  'Payment Hold Review': 'payment_hold_review',
  'Document Saving': 'document_saving',
  'Request Reassign': 'request_reassign',
  'Rejected Form': 'rejected_form',
  // Dubai Payment From Office Account workflow sections
  'Payment Details From Office Account':
    'dubai_payment_from_office_account_payment_details',
  Approver: 'dubai_payment_from_office_account_approver',
  'Payment Confirmation From Office Account':
    'dubai_payment_from_office_account_payment_confirmation',
  // Dubai Transfer Requisition workflow sections
  'Payment Details Transfer Requisition':
    'dubai_transfer_requisition_payment_details',
  'First Partner Approval': 'dubai_transfer_requisition_first_partner',
  'Second Partner Approval': 'dubai_transfer_requisition_second_partner',
  'Payment Confirmation Transfer':
    'dubai_transfer_requisition_payment_confirmation',
  // Overtime Approval workflow sections
  'Request Details': 'overtime_approval_request_details',
  'Line Manager Approval': 'overtime_approval_line_manager',
  'Finance Confirmation': 'overtime_approval_finance_confirmation',
  gift_hospitality_donation_uk_request_details: 'gift_hospitality_donation_uk_request_details',
  gift_hospitality_donation_uk_approval: 'gift_hospitality_donation_uk_approval',
  gift_hospitality_donation_dubai_request_details:
    'gift_hospitality_donation_dubai_request_details',
  gift_hospitality_donation_dubai_approval:
    'gift_hospitality_donation_dubai_approval',
  gift_hospitality_donation_dublin_request_details:
    'gift_hospitality_donation_dublin_request_details',
  gift_hospitality_donation_dublin_approval:
    'gift_hospitality_donation_dublin_approval',
  // PLOP_INJECT_BACKEND_SECTION_MAP
};

/**
 * Helper function to get workflow-specific section keys
 */
export const getWorkflowSectionKeys = (
  workflowType: PaymentWorkflowType,
): SectionKey[] => {
  switch (workflowType) {
    case PaymentWorkflowType.DubaiPaymentApproval:
      // payment_hold_review is conditional — only included when
      // resp_hold_payment === 'yes' via getSectionFlow
      return [
        'payment_details',
        'resp_lawyer',
        'first_partner',
        'second_partner',
        'payment_confirmation',
        'document_saving',
      ];
    case PaymentWorkflowType.DubaiPaymentFromOfficeAcc:
      return [
        'dubai_payment_from_office_account_payment_details',
        'dubai_payment_from_office_account_approver',
        'dubai_payment_from_office_account_payment_confirmation',
      ];
    case PaymentWorkflowType.DubaiTransferRequisition:
      return [
        'dubai_transfer_requisition_payment_details',
        'dubai_transfer_requisition_first_partner',
        'dubai_transfer_requisition_second_partner',
        'dubai_transfer_requisition_payment_confirmation',
      ];
    case PaymentWorkflowType.OvertimeApproval:
      return [
        'overtime_approval_request_details',
        'overtime_approval_line_manager',
        'overtime_approval_finance_confirmation',
      ];
    case PaymentWorkflowType.GiftHospitalityDonationUk:
      return ['gift_hospitality_donation_uk_request_details', 'gift_hospitality_donation_uk_approval'];
    case PaymentWorkflowType.GiftHospitalityDonationDubai:
      return [
        'gift_hospitality_donation_dubai_request_details',
        'gift_hospitality_donation_dubai_approval',
      ];
    case PaymentWorkflowType.GiftHospitalityDonationDublin:
      return [
        'gift_hospitality_donation_dublin_request_details',
        'gift_hospitality_donation_dublin_approval',
      ];
    // PLOP_INJECT_WORKFLOW_SECTION_KEYS
    default:
      return [
        'payment_details',
        'resp_lawyer',
        'first_partner',
        'second_partner',
        'payment_confirmation',
        'document_saving',
      ];
  }
};

/**
 * Helper function to get the first section for a workflow
 */
export const getWorkflowFirstSection = (
  workflowType: PaymentWorkflowType,
): SectionKey => {
  const sections = getWorkflowSectionKeys(workflowType);
  return sections[0] || 'payment_details';
};

/**
 * Fields for the payment details section
 */
export interface PaymentDetailsFields {
  client: string;
  client_code?: string;
  matter: string;
  matter_code?: string;
  interest: string;
  interest_options: string;
  payments_count: string;
  // payment_spreadsheet: string;
  payment_total: string;
  payment_attachment?: string;
  comments?: string;
  responsible_lawyer: string;
  first_partner: string;
  second_partner: string;
  second_partner_above_25k: string;
  second_partner_below_25k: string;
}

/**
 * Fields for the responsible lawyer section
 */
export interface RespLawyerFields {
  resp_approve: string;
  resp_reject_details: string;
  resp_verify_details: string;
  resp_verify_bank: string;
  resp_bank_confirmed_by: string;
  resp_verify_currency: string;
  resp_attachment: string;
  resp_hold_payment: string;
  resp_comments: string;
}

/**
 * Fields for the first partner section
 */
export interface FirstPartnerFields {
  first_approver: string;
  first_reject_details: string;
  first_verify_details: string;
  first_verify_bank: string;
  first_verify_currency: string;
  first_verify_reason: string;
  first_comments: string;
}

/**
 * Fields for the second partner section
 */
export interface SecondPartnerFields {
  second_approver: string;
  second_reject_details: string;
  second_verify_details: string;
  second_verify_bank: string;
  second_verify_currency: string;
  second_verify_reason: string;
  second_comments: string;
}

/**
 * Fields for the payment confirmation section
 */
interface PaymentConfirmationFields {
  payment_confirmation: string;
  payment_rejection_details: string;
  payment_upload_documentation: string;
  payment_comments: string;
  payment_total_interest: string;
}

/**
 * Fields for the Dubai Transfer Requisition payment confirmation section
 */
export interface TransferRequisitionPaymentConfirmationFields {
  transfer_requisition_payment_confirmation: string;
  transfer_requisition_payment_rejection_details: string;
  transfer_requisition_payment_upload_documentation: string;
  transfer_requisition_payment_comments: string;
  transfer_requisition_payment_total_interest: string;
}

/**
 * Fields for the document saving section
 */
export interface DocumentSavingFields {
  documents_saved: string;
  form_saved: string;
}

/**
 * Fields for the payment hold review section
 */
export interface PaymentHoldReviewFields {
  payment_hold_review: string;
}

/**
 * Union type for all possible section fields
 */
export type SectionFields =
  | PaymentDetailsFields
  | RespLawyerFields
  | FirstPartnerFields
  | SecondPartnerFields
  | PaymentConfirmationFields
  | TransferRequisitionPaymentConfirmationFields
  | DocumentSavingFields
  | PaymentHoldReviewFields;

export interface SectionStatus {
  isComplete: boolean;
  completedDate: string | null;
}

export interface WorkflowProgress {
  [key: string]: SectionStatus;
}

/**
 * Response type for involvement permissions API
 */
export interface InvolvementPermissionsResponse {
  workflowId: number;
  workflowName: string;
  createdBy: string;
  involvedUsers: Array<InvolvementUser>;
}

export interface InvolvementUser {
  userId: string;
  displayName: string;
  email: string;
  role: InvolvementRole;
  assignedSections: string[];
}
// ============= Involvement Types =============
export type InvolvementRole = 'Creator' | 'Assigned';
export interface InvolvedUser {
  userId: string;
  displayName: string;
  email: string;
  role: InvolvementRole;
  assignedSections: string[];
}

export interface InvolvementResponse {
  workflowId: number;
  workflowName: string;
  createdBy: string;
  involvedUsers: InvolvedUser[];
  userIsInvolved: boolean;
  userAssignedSections: string[];
}

/**
 * Checks if a user has authorization to access a specific section
 * @param userAssignedSections Array of section names assigned to the user from the backend
 * @param sectionKey The section key to check authorization for
 * @returns true if user has authorization, false otherwise
 */
export const hasSectionAuthorization = (
  userAssignedSections: string[],
  sectionKey: SectionKey,
): boolean => {
  if (!userAssignedSections || userAssignedSections.length === 0) {
    return false;
  }

  return userAssignedSections.some((assignedSection) => {
    if (assignedSection === sectionKey) {
      return true;
    }

    return backendSectionToSectionKey[assignedSection] === sectionKey;
  });
};

export const normalizeAssignedSectionKeys = (
  userAssignedSections: string[],
): string[] => {
  const keys = userAssignedSections.map(
    (assignedSection) =>
      backendSectionToSectionKey[assignedSection] ?? assignedSection,
  );
  return Array.from(new Set(keys));
};
// Update the existing InvolvementData type to use the new structure
export type InvolvementData = InvolvementResponse;
